import React, { useState, useEffect, useCallback } from 'react';
import { FaHome, FaRegBuilding, FaPaintRoller, FaBroom, FaBuilding, FaCouch, FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaWhatsapp, FaInstagram, FaFacebook, FaYoutube } from 'react-icons/fa';
import { MdEco, MdVerified } from 'react-icons/md';
import { BiCheckShield, BiTimeFive } from 'react-icons/bi';
import './App.css';
import Swal from 'sweetalert2';

// ──────────────────────────────────────────
// PARTICLES (light white for visibility)
// ──────────────────────────────────────────
const PARTICLES = Array.from({ length: 20 }, (_, i) => ({
  id: i,
  left: `${Math.random() * 100}%`,
  size: `${Math.random() * 4 + 2}px`,
  duration: `${Math.random() * 10 + 8}s`,
  delay: `${Math.random() * 8}s`,
  color: 'rgba(255, 255, 255, 0.45)',
}));

// ──────────────────────────────────────────
// SERVICES DATA — unique images per card
// img1-img3 → painting, img4-img6 → texture
// img7-img9 → cleaning variants
// videos: painting = video1,2,3 | cleaning = video4,5,6
// ──────────────────────────────────────────
const PAINTING_SERVICES = [
  {
    id: 'house-painting',
    name: 'House Painting',
    icon: <FaHome size={34} color="var(--slate-light)" />,
    image: '/img13.jpg',
    desc: 'Transform your home with premium interior & exterior painting. We use top-quality paints for a flawless, long-lasting finish.',
    features: ['Interior & Exterior', 'Premium Quality Paint', 'All Surfaces', 'Quick Dry Formula'],
    videoTitle: 'House Painting Service — AP Traders',
    video: '/video1.mp4',
  },
  {
    id: 'office-painting',
    name: 'Office Painting',
    icon: <FaRegBuilding size={34} color="var(--slate-light)" />,
    image: '/img9.jpg',
    desc: 'Professional commercial painting for offices, showrooms & corporate spaces. Minimal disruption, maximum results.',
    features: ['Commercial Grade', 'After-Hours Work', 'Modern Textures', 'Custom Colors'],
    videoTitle: 'Office Painting Service — AP Traders',
    video: '/video2.mp4',
  },
  {
    id: 'texture-coating',
    name: 'Texture & Coating',
    icon: <FaPaintRoller size={34} color="var(--slate-light)" />,
    image: '/img10.jpg',
    desc: 'Unique wall textures, waterproofing coatings & decorative finishes that add character and protection to your walls.',
    features: ['3D Textures', 'Waterproofing', 'Anti-fungal Coat', 'Designer Finishes'],
    videoTitle: 'Texture Coating Service — 1Kart Solutions',
    video: '/video3.mp4',
  },
];

const CLEANING_SERVICES = [
  {
    id: 'home-cleaning',
    name: 'Home Deep Cleaning',
    icon: <FaBroom size={34} color="var(--slate-light)" />,
    image: '/img1.jpg',
    desc: 'Complete deep cleaning for your home — every corner, surface & room gets a thorough professional clean.',
    features: ['All Rooms', 'Kitchen Deep Clean', 'Bathroom Sanitize', 'Eco-friendly Products'],
    videoTitle: 'Home Deep Cleaning — 1Kart Solutions',
    video: '/video4.mp4',
  },
  {
    id: 'office-cleaning',
    name: 'Office Cleaning',
    icon: <FaBuilding size={34} color="var(--slate-light)" />,
    image: '/img5.jpg',
    desc: 'Keep your workplace spotless and hygienic. Regular or one-time deep cleaning for offices, labs & commercial spaces.',
    features: ['Daily / Weekly', 'Workstation Clean', 'Sanitization', 'Disinfection Spray'],
    videoTitle: 'Office Cleaning —1Kart Solutions',
    video: '/video5.mp4',
  },
  {
    id: 'sofa-cleaning',
    name: 'Sofa & Carpet Cleaning',
    icon: <FaCouch size={34} color="var(--slate-light)" />,
    image: '/img3.jpg',
    desc: 'Professional upholstery & carpet cleaning using steam technology to remove deep stains, allergens & odours.',
    features: ['Steam Cleaning', 'Stain Removal', 'Odour Treatment', 'Fabric Safe'],
    videoTitle: 'Sofa & Carpet Cleaning — 1Kart Solutions',
    video: '/video6.mp4',
  },
];

// ──────────────────────────────────────────
// SCROLL REVEAL HOOK
// Re-observes whenever deps change (fixes toggle bug)
// ──────────────────────────────────────────
function useScrollReveal(deps = []) {
  useEffect(() => {
    const timer = setTimeout(() => {
      const elements = document.querySelectorAll('.reveal:not(.visible)');
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('visible');
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.1 }
      );
      elements.forEach((el) => observer.observe(el));
      return () => observer.disconnect();
    }, 50);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

// ──────────────────────────────────────────
// NAVBAR
// ──────────────────────────────────────────
function Navbar({ onNav }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const go = (s) => { setMenuOpen(false); onNav(s); };

  return (
    <nav className={`navbar${scrolled ? ' scrolled' : ''}`}>
      <div className="navbar-inner">
        <div className="logo" onClick={() => go('hero')}>
          <img src="/logo.png" alt="AP Traders" className="logo-img" />

        </div>

        <ul className={`nav-links${menuOpen ? ' open' : ''}`}>
          <li><button className="white-btn" onClick={() => document.getElementById('process').scrollIntoView({ behavior: 'smooth' })}>Book a Service</button></li>
        </ul>

        <button
          className="hamburger"
          onClick={() => setMenuOpen(m => !m)}
          aria-label="Toggle menu"
        >
          <span style={menuOpen ? { transform: 'rotate(45deg) translate(5px, 5px)' } : {}} />
          <span style={menuOpen ? { opacity: 0 } : {}} />
          <span style={menuOpen ? { transform: 'rotate(-45deg) translate(5px, -5px)' } : {}} />
        </button>
      </div>
    </nav >
  );
}

// ──────────────────────────────────────────
// HERO / BANNER
// ──────────────────────────────────────────
function Hero({ onNav }) {
  const openWhatsApp = () => {
    const phone = "919371200300";
    const message = "Hello, I would like to know more about your Painting & Cleaning Services.";

    // Mobile app
    if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      window.location.href =
        `whatsapp://send?phone=${phone}&text=${encodeURIComponent(message)}`;
    } else {
      // Desktop
      window.open(
        `https://web.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(message)}`,
        "_blank"
      );
    }
  };

  return (
    <section className="hero" id="hero">
      {/* <video
        autoPlay
        loop
        muted
        playsInline
        className="hero-video-bg"
        src="/1kart banner.mp4"
      />
      <div className="hero-video-overlay" /> */}

      {/* <div className="hero-rings">
        <div className="ring" />
        <div className="ring" />
        <div className="ring" />
      </div> */}

      {/* <div className="hero-particles">
        {PARTICLES.map(p => (
          <div
            key={p.id}
            className="particle"
            style={{
              left: p.left, width: p.size, height: p.size,
              background: p.color,
              animationDuration: p.duration,
              animationDelay: p.delay,
            }}
          />
        ))}
      </div> */}



      <div className="hero-content">


        <div className="hero-left">
        </div>

      </div>

      <div className="hero-btns">
        <button className="white-btn" onClick={() => document.getElementById('process').scrollIntoView({ behavior: 'smooth' })}>
          Book a Service
        </button>


      </div>
      {/* <div className="hero-scroll-indicator" onClick={() => onNav('services')}>
        <div className="scroll-mouse">
          <div className="scroll-dot" />
        </div>
        <span className="scroll-text">Scroll</span>
      </div> */}
    </section>
  );
}

// ──────────────────────────────────────────
// VIDEO MODAL — loops the video, plays on open
// ──────────────────────────────────────────
function VideoModal({ service, onClose }) {
  const videoRef = React.useRef(null);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    // auto-play when modal opens
    if (videoRef.current) {
      videoRef.current.play().catch(() => { });
    }
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className="video-modal">
      <div className="video-modal-backdrop" onClick={onClose} />
      <div className="video-modal-content">
        <div className="video-modal-header">
          <h3>{service.videoTitle}</h3>
          <button className="video-modal-close" onClick={onClose}>✕</button>
        </div>
        <video
          ref={videoRef}
          className="video-modal-player"
          src={service.video}
          loop
          autoPlay
          playsInline
          controls
        />
      </div>
    </div>
  );
}

// ──────────────────────────────────────────
// SERVICE CARD
// ──────────────────────────────────────────
function ServiceCard({ service, onPlay }) {
  return (
    <div className="service-card">
      <div className="service-img-wrap">
        <img src={service.image} alt={service.name} />
        <div className="service-img-overlay" />
        <div className="play-btn-overlay">
          <button
            className="play-btn"
            onClick={() => onPlay(service)}
            aria-label={`Watch ${service.name} video`}
          >
            ▶
          </button>
        </div>
      </div>

      <div className="service-body">
        <div className="service-header" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <div className="service-icon" style={{ marginBottom: 0 }}>{service.icon}</div>
          <h3 className="service-name" style={{ marginBottom: 0 }}>{service.name}</h3>
        </div>
        <p className="service-desc">{service.desc}</p>
        <div className="service-features">
          {service.features.map((f, i) => (
            <div className="service-feature" key={i}>{f}</div>
          ))}
        </div>
        <button className="service-watch-btn" onClick={() => onPlay(service)}>
          ▶ &nbsp;Watch Service Video
        </button>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────
// SERVICES SECTION
// Fix: reset reveal classes on toggle so re-animation works
// ──────────────────────────────────────────
function Services() {
  const [active, setActive] = useState('painting');
  const [videoService, setVideoService] = useState(null);

  // Re-run scroll reveal whenever tab changes
  useScrollReveal([active]);

  const handleToggle = useCallback((tab) => {
    if (tab === active) return;
    // Un-reveal cards so they can re-animate
    document.querySelectorAll('.service-card-wrap').forEach(el => {
      el.classList.remove('visible');
    });
    setActive(tab);
  }, [active]);

  const services = active === 'painting' ? PAINTING_SERVICES : CLEANING_SERVICES;

  return (
    <section className="services" id="services">
      <div className="container">
        <div className="services-header reveal">
          {/* <div className="section-tag">What We Offer</div> */}
          <h2 className="section-title">
            Our <span>Premium</span> Services
          </h2>
          <p style={{ color: 'var(--text-secondary)', marginTop: '12px', fontSize: '16px' }}>
            Expert-level painting &amp; cleaning tailored for your home, office &amp; beyond.
          </p>
        </div>

        {/* TOGGLE */}
        <div className="services-toggle reveal">
          <button
            id="toggle-painting"
            className={`toggle-btn${active === 'painting' ? ' active' : ''}`}
            onClick={() => handleToggle('painting')}
          >
            Painting
          </button>
          <button
            id="toggle-cleaning"
            className={`toggle-btn${active === 'cleaning' ? ' active' : ''}`}
            onClick={() => handleToggle('cleaning')}
          >
            Cleaning
          </button>
        </div>

        {/* CARDS */}
        <div className="services-grid">
          {services.map((s, i) => (
            <div
              key={s.id}
              className={`reveal service-card-wrap reveal-delay-${i + 1}`}
            >
              <ServiceCard service={s} onPlay={setVideoService} />
            </div>
          ))}
        </div>
      </div>

      {videoService && (
        <VideoModal service={videoService} onClose={() => setVideoService(null)} />
      )}
    </section>
  );
}




function HowWeWork() {
  const [active, setActive] = useState("cleaning");
  const [expandedCard, setExpandedCard] = useState(null);

  useScrollReveal([active]);

  const cleaningCards = [
    {
      id: "1bhk-n",
      name: "1 BHK (Non-Furnished)",
      price: "₹2,999",
      image: "/1bhk N.png",
      tag: "Home Cleaning",
      desc: "Complete deep cleaning for 1 BHK non-furnished home",
      features: [
        "Bedroom & Hall Floor Machine Scrubbing",
        "Kitchen Tiles, Countertop & Sink Degreasing",
        "Bathroom Deep Scrubbing & Disinfection",
        "Cobweb Removal, Fans & Window Cleaning"
      ]
    },
    {
      id: "1bhk-f",
      name: "1 BHK (Furnished)",
      price: "₹3,999",
      image: "/1bhk F.png",
      tag: "Home Cleaning",
      desc: "Thorough deep clean including furniture & appliances",
      features: [
        "Sofa, Mattress & Curtain Dry Vacuuming",
        "Inside & Outside Modular Kitchen Cabinet Clean",
        "Appliance Exterior Sanitization (Fridge/Microwave)",
        "Deep Bathroom Scrubbing & Floor Polishing"
      ]
    },
    {
      id: "2bhk-n",
      name: "2 BHK (Non-Furnished)",
      price: "₹3,999",
      image: "/2bhk N.png",
      tag: "Home Cleaning",
      desc: "Full sanitization and deep cleaning for 2 BHK non-furnished",
      features: [
        "Full 2 BHK Floor Scrubbing & Stain Removal",
        "Both Bathrooms Disinfection & Tile Cleaning",
        "Balcony, Grills, Doors & Window Wash",
        "Hard-to-reach dust removal & Light fixture clean"
      ]
    },
    {
      id: "2bhk-f",
      name: "2 BHK (Furnished)",
      price: "₹4,999",
      image: "/2bhk F.png",
      tag: "Home Cleaning",
      desc: "Deep cleaning for 2 BHK furnished space with upholstery treatment",
      features: [
        "Complete Upholstery & Carpet Steam Clean",
        "Modular Kitchen Deep Degreasing & Cabinet Clean",
        "Furniture Vacuuming & Wood Polishing",
        "Sanitization of All 2 Bathrooms & Balconies"
      ]
    },
    {
      id: "3bhk-n",
      name: "3 BHK (Non-Furnished)",
      price: "₹4,999",
      image: "/3bhk N.png",
      tag: "Home Cleaning",
      desc: "Comprehensive deep clean for 3 BHK empty home",
      features: [
        "Extensive 3 BHK Floor Polishing & Scrubbing",
        "All 3 Bathrooms Deep Acid & Anti-Bacterial Wash",
        "Large Balconies, Glass Windows & Grills Cleaned",
        "Complete Wall Dusting & Switchboard Wipe"
      ]
    },
    {
      id: "3bhk-f",
      name: "3 BHK (Furnished)",
      price: "₹5,999",
      image: "/3bhk F.png",
      tag: "Home Cleaning",
      desc: "Premium full-house cleaning & sanitization for 3 BHK furnished",
      features: [
        "Full Home Furniture Polish & Upholstery Care",
        "Kitchen Chimney, Exhaust Fan & Cabinet Clean",
        "Deep Sanitization of All 3 Bathrooms & Bedrooms",
        "Mirror, Glass & Chandelier Spotless Wipe"
      ]
    },
    {
      id: "office-clean",
      name: "Office Deep Cleaning",
      price: "Site Visit Required",
      image: "/OC.png",
      tag: "Commercial Space",
      desc: "Custom commercial workspace sanitization & deep cleaning",
      features: ["Desks, Chairs & Workstation Sanitization", "Commercial Floor Buffing & Carpet Wash", "Restroom & Pantry Hygiene Treatment", "Facade Glass & Meeting Room Deep Clean"]
    },
    {
      id: "sofa-clean",
      name: "5 Seater Sofa (3+2)",
      price: "₹599",
      image: "/sc.png",
      tag: "Sofa Cleaning",
      desc: "Steam cleaning, stain removal & fabric care for 5 seater sofa",
      features: ["High-Pressure Steam Extraction Wash", "Tough Food & Ink Stain Removal", "Fabric Odour & Allergen Elimination", "Quick-Dry Anti-Bacterial Conditioning"]
    }
  ];

  const paintingCards = [
    {
      id: "essential",
      name: "Essential Finish",
      price: "₹9/sq.ft.",
      image: "/Essential.png",
      tag: "Budget Painting",
      desc: "Smooth coat finish for interior walls with smooth finish",
      features: ["Wall Putty Touchup & Sanding", "1 Coat Acrylic Primer Application", "2 Coats Quality Emulsion Paint", "Smooth Matte Budget Interior Finish"]
    },
    {
      id: "classic",
      name: "Classic Finish",
      price: "₹12/sq.ft.",
      image: "/Classic.png",
      tag: "Durable Coating",
      desc: "Long-lasting washable paint finish for home & office",
      features: ["Wall Putty Touchup & Sanding", "High Adhesion Acrylic Primer Coat", "2 Coats Premium Washable Paint", "Superior Washability & Color Retention"]
    },
    {
      id: "premium",
      name: "Premium Finish",
      price: "₹15/sq.ft.",
      image: "/Premium.png",
      tag: "Silk & Matt Finish",
      desc: "High-sheen washable finish with vibrant color retention",
      features: ["Wall Putty Touchup & Sanding", "Anti-Fungal & Anti-Algae Primer Coat", "2 Coats Silk Sheen Emulsion Paint", "Dirt Resistant & Rich Elegant Look"]
    },
    {
      id: "luxury",
      name: "Luxury Finish",
      price: "₹20/sq.ft.",
      image: "/luxury.png",
      tag: "Royale Ultra Shine",
      desc: "Ultra-luxurious rich finish with anti-bacterial protection",
      features: ["Royale Level Glass-Like Surface Polish", "Teflon Surface Protection & Washable", "Rich High-Gloss Sheen & Anti-Bacterial", "3 Years Free Touchup & Warranty"]
    },
    {
      id: "texture",
      name: "Designer Texture Finish",
      price: "Custom Quote",
      image: "/texture.png",
      tag: "3D Texture & Art",
      desc: "Custom decorative textures, accent walls & designer patterns",
      features: ["3D Wall Art & Metallic Texture Coats", "Custom Color & Stucco Pattern Designs", "Velvet & Marble Finish Accent Walls", "Free Designer On-Site Consultation"]
    }
  ];

  const handleBookCard = (e, item) => {
    e.stopPropagation();

    const phone = "919371200300";

    const message = `Hello,

I am interested in the ${item.name} package (${item.price}).

Please share complete details about this service.

Thank you.`;

    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;

    window.open(url, "_blank");
  };

  const handleCardClick = (cardId) => {
    setExpandedCard(expandedCard === cardId ? null : cardId);
  };

  const cards = active === "cleaning" ? cleaningCards : paintingCards;

  return (
    <section className="workflow-section" id="process">
      <div className="container">
        <div className="services-header reveal visible">
          {/* <div className="section-tag">EXPLORE PACKAGES</div> */}
          <h2 className="section-title">
            Choose <span>Your Perfect</span> Package
          </h2>
          <p style={{ color: "var(--text-secondary)", marginTop: "12px", fontSize: "16px" }}>
            Explore our cleaning & painting packages, designed for every space and budget.
          </p>
        </div>

        <div className="services-toggle reveal visible">
          <button
            className={`toggle-btn ${active === "cleaning" ? "active" : ""}`}
            onClick={() => setActive("cleaning")}
          >
            Cleaning
          </button>
          <button
            className={`toggle-btn ${active === "painting" ? "active" : ""}`}
            onClick={() => setActive("painting")}
          >
            Painting
          </button>
        </div>

        <div className={active === "cleaning" ? "pronto-cleaning-grid" : "pronto-painting-grid"}>
          {cards.map((card) => (
            <div
              key={card.id}
              className={`pronto-card ${expandedCard === card.id ? 'mobile-expanded' : ''}`}
              onClick={() => handleCardClick(card.id)}
            >
              <div className="pronto-card-img-wrap">
                <img src={card.image} alt={card.name} />
              </div>

              <div className="pronto-card-content">
                <div className="pronto-card-meta">
                  <h4 className="pronto-card-title">{card.name}</h4>
                  <span className="pronto-card-price-badge">{card.price}</span>
                </div>
                <div className="pronto-explore-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCardClick(card.id);
                  }}
                >
                  Know More
                </div>
              </div>

              <div className={`pronto-card-hover-info ${expandedCard === card.id ? 'mobile-visible' : ''}`}>
                <div>
                  <span className="pronto-tag">{card.tag}</span>
                  <h5 className="pronto-hover-title">{card.name}</h5>
                  <p className="pronto-hover-desc">{card.desc}</p>
                  <ul className="pronto-hover-features">
                    {card.features.map((feat, idx) => (
                      <li key={idx}>{feat}</li>
                    ))}
                  </ul>
                </div>

                <div className="pronto-hover-footer">
                  <span className="pronto-price">{card.price}</span>
                  <button className="pronto-book-btn" onClick={(e) => handleBookCard(e, card)}>
                    Book Now →
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}









// ──────────────────────────────────────────
// ABOUT US
// ──────────────────────────────────────────
function About() {
  useScrollReveal([]);

  return (
    <section className="about" id="about" style={{ backgroundImage: "url('/img15.jpg')" }}>
      <div className="container">
        <div className="about-inner">
          <div className="about-image-side reveal">
            <div className="about-image-stack">
              <img
                className="about-main-img img-1"
                src="/img12.jpg"
                alt="Painting service"
              />
              <img
                className="about-main-img img-2"
                src="/img17.jpg"
                alt="Cleaning service"
              />
            </div>
          </div>


          <div className="about-content">
            {/* <div className="section-tag-inv reveal">About 1Kart Solutions</div> */}

            <h2 className="section-title about-title reveal reveal-delay-1">
              Premium <span>Excellence</span>
            </h2>

            <div className="about-mobile-image">

              <div className="about-image-stack">
                <img src="/img12.jpg" alt="" className="img-1" />
                <img src="/img1.jpg" alt="" className="img-2" />
              </div>

            </div>


            <p className="about-description reveal reveal-delay-1">
              1Kart Solution provides painting and cleaning services focused on quality workmanship, organised execution and customer convenience. Whether it's refreshing interiors, enhancing exterior surfaces or delivering complete deep cleaning, every project is handled with attention to detail.
            </p>

            <p className="about-description reveal reveal-delay-2" style={{ marginTop: '-14px' }}>
              From residential homes and apartments to offices, commercial establishments and industrial spaces, our goal is to deliver reliable solutions that improve the appearance, cleanliness and overall value of every property.
            </p>



            <div className="hero-btns reveal reveal-delay-4">

            </div>
          </div>
        </div>
      </div>
    </section >
  );
}

// ──────────────────────────────────────────
// CONTACT FORM
// ──────────────────────────────────────────
function Contact() {
  const [form, setForm] = useState({
    name: '', phone: '', email: '', service: '', address: '', message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData();
    formData.append('_subject', `New Booking Request from ${form.name} — AP Traders`);
    formData.append('_template', 'table');
    formData.append('_captcha', 'false');
    formData.append('Name', form.name);
    formData.append('Phone', form.phone);
    formData.append('Email', form.email);
    formData.append('Service', form.service);
    formData.append('Address', form.address);
    formData.append('Message', form.message || 'N/A');

    try {
      const res = await fetch('https://formsubmit.co/ajax/digisahyadriit@gmail.com', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: formData,
      });
      const data = await res.json();
      if (data.success === 'true' || data.success === true) {
        Swal.fire({
          title: 'Success!',
          text: 'Your booking request has been sent to our email.',
          icon: 'success',
          confirmButtonColor: '#6D7697',
          confirmButtonText: 'OK'
        });
        setForm({ name: '', phone: '', email: '', service: '', address: '', message: '' });
      } else {
        Swal.fire({
          title: 'Error!',
          text: 'Something went wrong. Please try again or call us directly.',
          icon: 'error',
          confirmButtonColor: '#6D7697'
        });
      }
    } catch (err) {
      Swal.fire({
        title: 'Network Error!',
        text: 'Please check your connection and try again.',
        icon: 'error',
        confirmButtonColor: '#6D7697'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="contact" id="contact">
      <div className="container">
        <div className="contact-inner">
          {/* LEFT INFO */}
          <div className="contact-info">
            {/* <div className="section-tag reveal">Get In Touch</div> */}
            <h2 className="section-title reveal" style={{ marginBottom: '16px' }}>
              Book Your <span>Service</span> Today
            </h2>
            <p className="reveal reveal-delay-1">
              Ready to transform your space? Fill the form and our team will reach out within 2 hours to confirm your booking.
            </p>

            <div className="contact-details">
              <div className="contact-item reveal reveal-delay-2" onClick={() => window.location.href = 'tel:+919371200300'} style={{ cursor: 'pointer' }}>
                <div className="contact-item-icon"><FaPhoneAlt color="var(--slate-light)" size={20} /></div>
                <div className="contact-item-text">
                  <strong>Call / WhatsApp</strong>
                  <span>+91 9371 200 300</span>
                </div>
              </div>
              <div className="contact-item reveal reveal-delay-3" onClick={() => window.location.href = 'mailto:info@1kartsolution.com'} style={{ cursor: 'pointer' }}>
                <div className="contact-item-icon"><FaEnvelope color="var(--slate-light)" size={20} /></div>
                <div className="contact-item-text">
                  <strong>Email Us</strong>
                  <span>info@1kartsolution.com</span>
                </div>
              </div>
              <div className="contact-item reveal reveal-delay-4" onClick={() => window.open('https://maps.app.goo.gl/VnfPcuF6eRmgRjTc9?g_st=iwb', '_blank')} style={{ cursor: 'pointer' }}>
                <div className="contact-item-icon"><FaMapMarkerAlt color="var(--slate-light)" size={20} /></div>
                <div className="contact-item-text">
                  <strong>Our Office Address</strong>
                  <span>1Kart Solution, Near Malu High School, Sangli, Maharashtra, India</span>
                </div>
              </div>
            </div>
          </div>

          {/* FORM */}
          <div className="contact-form-wrap reveal">
            <>
              <h3 className="form-title">Request a Service</h3>
              <form onSubmit={handleSubmit} id="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Full Name</label>
                    <input
                      id="name" name="name" type="text"
                      placeholder="Your full name"
                      pattern="[A-Za-z\s]+"
                      title="Only letters and spaces are allowed"
                      value={form.name} onChange={handleChange} required minLength={3}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">Contact Number</label>
                    <input
                      id="phone" name="phone" type="tel"
                      placeholder="10-digit number"
                      pattern="[0-9]{10,15}"
                      title="Only numbers are allowed (10-15 digits)"
                      value={form.phone} onChange={handleChange} required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email Address</label>
                    <input
                      id="email" name="email" type="email"
                      placeholder="you@example.com"
                      value={form.email} onChange={handleChange} required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="service">Select Service</label>
                    <select id="service" name="service" value={form.service} onChange={handleChange} required>
                      <option value="" disabled>Choose a service...</option>
                      <optgroup label="Painting">
                        <option value="house-painting">House Painting</option>
                        <option value="office-painting">Office Painting</option>
                        <option value="texture-coating">Texture &amp; Coating</option>
                      </optgroup>
                      <optgroup label="Cleaning">
                        <option value="home-cleaning">Home Deep Cleaning</option>
                        <option value="office-cleaning">Office Cleaning</option>
                        <option value="sofa-cleaning">Sofa &amp; Carpet Cleaning</option>
                      </optgroup>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="address">Address</label>
                  <input
                    id="address" name="address" type="text"
                    placeholder="Flat / Building / Area / City"
                    value={form.address} onChange={handleChange} required minLength={5}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message (Optional)</label>
                  <textarea
                    id="message" name="message"
                    placeholder="Tell us more about your requirements..."
                    value={form.message} onChange={handleChange} required
                  />
                </div>

                <button type="submit" className="submit-btn" disabled={loading}>
                  {loading
                    ? <><span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span> Sending...</>
                    : <>Submit Booking Request</>
                  }
                </button>
              </form>
            </>
          </div>
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────
// FOOTER
// ──────────────────────────────────────────
function Footer({ onNav }) {
  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-inner">
          {/* BRAND */}
          <div className="footer-brand">
            <div className="logo" onClick={() => onNav('hero')}>
              <img src="/logo.png" alt="AP Traders" className="logo-img" />
            </div>
            <p>Sangli's trusted partner for painting &amp; cleaning services. Excellence in every stroke &amp; sparkle.</p>
          </div>

          {/* CONTACT LIST */}
          <div className="footer-col">
            <h4 style={{ textAlign: 'center' }}>Contact Us</h4>
            <ul className="footer-contact-list">
              <li>
                <a href="tel:+919371200300">
                  <span className="f-icon"><FaPhoneAlt size={16} /></span>
                  +91 9371 200 300
                </a>
              </li>
              <li>
                <a href="mailto:info@1kartsolution.com">
                  <span className="f-icon"><FaEnvelope size={16} /></span>
                  info@1kartsolution.com
                </a>
              </li>
              <li>
                <a href="https://maps.app.goo.gl/VnfPcuF6eRmgRjTc9?g_st=iwb" target="_blank" rel="noopener noreferrer">
                  <span className="f-icon"><FaMapMarkerAlt size={16} /></span>
                  1Kart Solution, Near Malu High School, Sangli, Maharashtra, India
                </a>
              </li>
            </ul>
          </div>

          {/* FOLLOW US & MAP */}
          <div className="footer-col footer-social">
            <h4 style={{ textAlign: 'center' }}>Follow Us</h4>
            <div className="social-row" style={{ marginBottom: '12px' }}>
              <a
                className="social-btn"
                href="https://www.instagram.com/1_kart_solution/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaInstagram size={20} />
              </a>

              <a
                className="social-btn"
                href="https://www.facebook.com/1kartsolution/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaFacebook size={20} />
              </a>
            </div>

            {/* EMBEDDED MAP */}
            {/* <div
              className="footer-map-container"
              onClick={() => window.open('https://maps.app.goo.gl/VnfPcuF6eRmgRjTc9?g_st=iwb', '_blank')}
              style={{ cursor: 'pointer', textAlign: 'center', marginTop: '6px' }}
            >
              <iframe
                title="AP Traders Location"
                src="https://maps.google.com/maps?q=AP%20Traders%2C%20Malu%20High%20School%2C%20Sangli%2C%20Maharashtra%2C%20India&t=&z=14&ie=UTF8&iwloc=&output=embed"
                width="280"
                height="130"
                style={{ border: 0, borderRadius: '12px', pointerEvents: 'none' }}
                loading="lazy"
              />
              <div style={{ fontSize: '12px', color: '#c0a8ff', fontWeight: '600', marginTop: '4px' }}>
                📍 Click to Open Location Map
              </div>
            </div> */}
          </div>
        </div>

        <div className="footer-bottom">
          <p>© 2026 1Kart Solution. All rights reserved.</p>
          <p className="footer-author">
            Designed and Developed by{" "}
            <a
              href="https://sahyadritech.in/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <strong>SahyadriTech</strong>
            </a>
          </p>
        </div>
      </div>
    </footer >
  );
}

// ──────────────────────────────────────────
// WHATSAPP FLOATING BUTTON
// ──────────────────────────────────────────
function WhatsAppFloat() {
  const [showTip, setShowTip] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setShowTip(true), 3000);
    const t2 = setTimeout(() => setShowTip(false), 7000);
    return () => { clearTimeout(t); clearTimeout(t2); };
  }, []);

  return (
    <div className="whatsapp-float">
      {showTip && (
        <div className="whatsapp-tooltip" style={{ position: 'relative' }}>
          Chat with us!
        </div>
      )}
      <a
        className="whatsapp-float-btn"
        href="https://wa.me/919371200300?text=Hello,%20I%20want%20to%20know%20more%20about%20your%20painting%20and%20cleaning%20services."
        target="_blank"
        rel="noreferrer"
        aria-label="Chat on WhatsApp"
        onMouseEnter={() => setShowTip(true)}
        onMouseLeave={() => setShowTip(false)}
      >
        <FaWhatsapp size={32} color="#fff" />
      </a>
    </div>
  );
}



// map section


function LocationMap() {
  return (
    <section className="location-map">
      <div className="container">

        <div className="services-header reveal">
          {/* <div className="section-tag">
            Find Us
          </div> */}

          <h2 className="section-title">
            <div className="pronto-arrow"></div>
            Visit Our <span>Location</span>
          </h2>

          <p>
            Easily locate our office and get directions with Google Maps.
          </p>
        </div>

        <div
          className="location-map-box"
          onClick={() =>
            window.open(
              "https://maps.app.goo.gl/VnfPcuF6eRmgRjTc9?g_st=iwb",
              "_blank"
            )
          }
        >
          <iframe
            title="1Kart Solutions"
            src="https://maps.google.com/maps?q=AP%20Traders%20Malu%20High%20School%20Sangli&t=&z=15&ie=UTF8&iwloc=&output=embed"
            loading="lazy"
          />
          {/* 
          <div className="location-overlay">
            Click to Open Google Maps
          </div> */}
        </div>

      </div>
    </section>
  );
}





// ──────────────────────────────────────────
// APP ROOT
// ──────────────────────────────────────────
function App() {
  // Initial scroll reveal for static sections
  useScrollReveal([]);

  // Dynamically generate the AP favicon
  // useEffect(() => {
  //   try {
  //     const canvas = document.createElement('canvas');
  //     canvas.width = 64;
  //     canvas.height = 64;
  //     const ctx = canvas.getContext('2d');
  //     if (ctx) {
  //       // Create gradient background matching AP theme
  //       const grad = ctx.createLinearGradient(0, 0, 64, 64);
  //       grad.addColorStop(0, '#6D7697');
  //       grad.addColorStop(1, '#545a78');
  //       ctx.fillStyle = grad;

  //       // Draw rounded rectangle
  //       const r = 16; // radius
  //       ctx.beginPath();
  //       ctx.moveTo(r, 0);
  //       ctx.lineTo(64 - r, 0);
  //       ctx.quadraticCurveTo(64, 0, 64, r);
  //       ctx.lineTo(64, 64 - r);
  //       ctx.quadraticCurveTo(64, 64, 64 - r, 64);
  //       ctx.lineTo(r, 64);
  //       ctx.quadraticCurveTo(0, 64, 0, 64 - r);
  //       ctx.lineTo(0, r);
  //       ctx.quadraticCurveTo(0, 0, r, 0);
  //       ctx.closePath();
  //       ctx.fill();

  //       // Draw white text "AP"
  //       ctx.fillStyle = '#ffffff';
  //       ctx.font = 'bold 32px "Montserrat", sans-serif';
  //       ctx.textAlign = 'center';
  //       ctx.textBaseline = 'middle';
  //       ctx.fillText('AP', 32, 32);

  //       // Find or create favicon link tag
  //       let link = document.querySelector("link[rel*='icon']");
  //       if (!link) {
  //         link = document.createElement('link');
  //         link.rel = 'icon';
  //         document.getElementsByTagName('head')[0].appendChild(link);
  //       }
  //       link.href = canvas.toDataURL('image/png');
  //     }
  //   } catch (e) {
  //     console.error("Failed to generate dynamic favicon", e);
  //   }
  // }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="App">
      <Navbar onNav={scrollTo} />
      <Hero onNav={scrollTo} />
      <HowWeWork />
      <Services />
      <About />
      <Contact />
      <LocationMap />
      <Footer onNav={scrollTo} />
      <WhatsAppFloat />
    </div>
  );
}

export default App;
